/* German text-to-speech for the Hören game.
 *
 * Why this is a module and not two lines inline: `speechSynthesis` is
 * unreliable inside an embedded WebView. On iOS WKWebView the voice list is
 * often empty on first call, and on some Android WebView builds it is missing
 * outright. So this prefers the native engine when running under Capacitor,
 * and falls back to the browser API on the web.
 *
 * For the native path:  npm i @capacitor-community/text-to-speech
 * It is optional. We read it off Capacitor's global plugin registry rather than
 * importing it, so the web build has no dependency on it at all and the bundler
 * never has to resolve it.
 */

let webVoiceReady = false;
let webVoiceFound = null;   // null = unknown, false = no German voice

const isNative = () =>
  typeof window !== "undefined" &&
  window.Capacitor &&
  typeof window.Capacitor.isNativePlatform === "function" &&
  window.Capacitor.isNativePlatform();

/* Capacitor registers installed plugins on window.Capacitor.Plugins, so this
   resolves without an import and returns undefined when the plugin is absent. */
function getNative() {
  if (!isNative()) return null;
  return window.Capacitor?.Plugins?.TextToSpeech || null;
}

/* The web voice list is populated asynchronously; ask early so the first
   Hören question isn't the one that discovers it's empty. */
export function primeVoices() {
  try {
    if (!window.speechSynthesis) return;
    const load = () => {
      const vs = window.speechSynthesis.getVoices();
      if (vs && vs.length) {
        webVoiceReady = true;
        webVoiceFound = vs.some((v) => v.lang && v.lang.toLowerCase().startsWith("de"));
      }
    };
    load();
    window.speechSynthesis.addEventListener?.("voiceschanged", load);
  } catch {
    /* no speech support at all */
  }
}

/**
 * Speak a German string.
 * @returns {Promise<boolean>} false when nothing could be spoken.
 */
export async function speak(text) {
  const native = getNative();
  if (native) {
    try {
      await native.stop().catch(() => {});
      await native.speak({ text, lang: "de-DE", rate: 0.95, category: "playback" });
      return true;
    } catch {
      /* fall through to the web API */
    }
  }
  try {
    if (!window.speechSynthesis) return false;
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "de-DE";
    u.rate = 0.88;
    const v = window.speechSynthesis
      .getVoices()
      .find((x) => x.lang && x.lang.toLowerCase().startsWith("de"));
    if (v) u.voice = v;
    if (!webVoiceReady) webVoiceFound = !!v;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(u);
    return true;
  } catch {
    return false;
  }
}

export async function stopSpeaking() {
  const native = getNative();
  if (native) {
    try {
      await native.stop();
      return;
    } catch { /* ignore */ }
  }
  try {
    window.speechSynthesis?.cancel();
  } catch { /* ignore */ }
}

/** null = not yet known, true/false = whether a German voice exists. */
export const germanVoiceAvailable = () => (isNative() ? true : webVoiceFound);
